// src/components/transactions/TransactSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, TrendingUp, TrendingDown, Wallet, Filter, Download,
  Calendar, Search, RefreshCw, BarChart3, PieChart, ArrowUpRight,
  ArrowDownRight, FileText, Image, Trash2, Edit, Eye
} from 'lucide-react';
import { transactionService } from '../../../services/transactionService';
import { TransactionForm } from '../forms/TransactionForm';

export const TransactSection = ({ bookId, bookData }) => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    status: 'all',
    dateRange: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list or grid

  useEffect(() => {
    loadData();
  }, [bookId]);

  useEffect(() => {
    if (transactions.length > 0) {
      calculateAnalytics();
    }
  }, [transactions, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transactionsData, categoriesData] = await Promise.all([
        transactionService.getTransactions(bookId),
        transactionService.getBookCategories(bookId)
      ]);

      setTransactions(transactionsData);
      
      // Flatten categories for filtering
      const allCategories = [
        ...categoriesData.income,
        ...categoriesData.expense
      ];
      setCategories(allCategories);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = async () => {
    try {
      const analyticsData = await transactionService.getTransactionAnalytics(bookId);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error calculating analytics:', error);
    }
  };

  const handleSaveTransaction = async (transactionData) => {
    try {
      if (selectedTransaction) {
        await transactionService.updateTransaction(
          selectedTransaction.id,
          transactionData
        );
      } else {
        await transactionService.createTransaction(bookId, transactionData);
      }
      
      await loadData();
      setShowForm(false);
      setSelectedTransaction(null);
    } catch (error) {
      console.error('Error saving transaction:', error);
      throw error;
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      await transactionService.deleteTransaction(transactionId, bookId);
      await loadData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowForm(true);
  };

  const filteredTransactions = transactions.filter(transaction => {
    // Type filter
    if (filters.type !== 'all' && transaction.type !== filters.type) {
      return false;
    }

    // Category filter
    if (filters.category !== 'all' && transaction.category !== filters.category) {
      return false;
    }

    // Status filter
    if (filters.status !== 'all' && transaction.status !== filters.status) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        transaction.description?.toLowerCase().includes(searchLower) ||
        transaction.reference?.toLowerCase().includes(searchLower) ||
        transaction.notes?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.icon || 'Tag';
  };

  const formatCurrency = (amount) => {
    return `R ${parseFloat(amount).toLocaleString('en-ZA', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Analytics Cards */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Financial Manager</h2>
          <p className="text-gray-600 mt-1">Track income, expenses, and budgets</p>
        </div>
        <button
          onClick={() => {
            setSelectedTransaction(null);
            setShowForm(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center gap-2"
          style={{
            background: bookData?.styling?.primaryColor
          }}
        >
          <Plus className="w-5 h-5" />
          New Transaction
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="w-6 h-6" />
              </div>
              <ArrowUpRight className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-medium opacity-90">Total Income</h3>
            <p className="text-3xl font-bold mt-2">
              {formatCurrency(analytics.totalIncome)}
            </p>
            <p className="text-xs opacity-75 mt-2">
              {analytics.byType.income} transactions
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingDown className="w-6 h-6" />
              </div>
              <ArrowDownRight className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-medium opacity-90">Total Expenses</h3>
            <p className="text-3xl font-bold mt-2">
              {formatCurrency(analytics.totalExpenses)}
            </p>
            <p className="text-xs opacity-75 mt-2">
              {analytics.byType.expense} transactions
            </p>
          </div>

          <div className={`bg-gradient-to-br rounded-2xl p-6 text-white shadow-lg ${
            analytics.netBalance >= 0
              ? 'from-blue-500 to-indigo-600'
              : 'from-orange-500 to-red-600'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Wallet className="w-6 h-6" />
              </div>
              {analytics.netBalance >= 0 ? (
                <TrendingUp className="w-5 h-5" />
              ) : (
                <TrendingDown className="w-5 h-5" />
              )}
            </div>
            <h3 className="text-sm font-medium opacity-90">Net Balance</h3>
            <p className="text-3xl font-bold mt-2">
              {formatCurrency(Math.abs(analytics.netBalance))}
            </p>
            <p className="text-xs opacity-75 mt-2">
              {analytics.netBalance >= 0 ? 'Surplus' : 'Deficit'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <BarChart3 className="w-6 h-6" />
              </div>
              <PieChart className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-medium opacity-90">Total Transactions</h3>
            <p className="text-3xl font-bold mt-2">
              {analytics.totalTransactions}
            </p>
            <p className="text-xs opacity-75 mt-2">
              {analytics.byStatus.completed} completed
            </p>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="income">Income Only</option>
            <option value="expense">Expenses Only</option>
          </select>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Refresh */}
          <button
            onClick={loadData}
            className="px-4 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-16">
            <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No transactions found
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.type !== 'all' || filters.category !== 'all'
                ? 'Try adjusting your filters'
                : 'Start by creating your first transaction'}
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
            >
              Create Transaction
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredTransactions.map((transaction) => {
              const category = categories.find(c => c.id === transaction.category);
              
              return (
                <div
                  key={transaction.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Icon & Details */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-xl ${
                        transaction.type === 'income'
                          ? 'bg-green-100'
                          : 'bg-red-100'
                      }`}>
                        {transaction.type === 'income' ? (
                          <TrendingUp className={`w-6 h-6 ${
                            transaction.type === 'income'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`} />
                        ) : (
                          <TrendingDown className={`w-6 h-6 ${
                            transaction.type === 'income'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {transaction.description}
                          </h4>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xl font-bold ${
                              transaction.type === 'income'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}>
                              {transaction.type === 'income' ? '+' : '-'}
                              {formatCurrency(transaction.amount)}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-2">
                          {category && (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-lg">
                              <span>{category.name}</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {formatDate(transaction.transactionDate)}
                          </span>
                          {transaction.reference && (
                            <span className="flex items-center gap-1.5">
                              <FileText className="w-4 h-4" />
                              {transaction.reference}
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </div>

                        {transaction.notes && (
                          <p className="text-sm text-gray-600 mb-2">
                            {transaction.notes}
                          </p>
                        )}

                        {/* Attachments */}
                        {transaction.attachments?.length > 0 && (
                          <div className="flex items-center gap-2 mt-3">
                            <div className="flex items-center gap-1.5 text-sm text-gray-500">
                              <FileText className="w-4 h-4" />
                              <span>{transaction.attachments.length} attachment(s)</span>
                            </div>
                            <div className="flex gap-2">
                              {transaction.attachments.slice(0, 3).map((file, idx) => (
                                <a
                                  key={idx}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                  title={file.fileName}
                                >
                                  {file.fileType.startsWith('image/') ? (
                                    <Image className="w-4 h-4 text-gray-600" />
                                  ) : (
                                    <FileText className="w-4 h-4 text-gray-600" />
                                  )}
                                </a>
                              ))}
                              {transaction.attachments.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-600">
                                  +{transaction.attachments.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditTransaction(transaction)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transaction Form Modal */}
      {showForm && (
        <TransactionForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedTransaction(null);
          }}
          onSave={handleSaveTransaction}
          transaction={selectedTransaction}
          bookId={bookId}
          categories={categories}
        />
      )}
    </div>
  );
};